// Group a cycle's renders into design concepts. Two sources:
//   1. The synced cycle (resultFileNames → content-addressed blobs in the store).
//   2. Fallback: the still-completed backend job (when the phone never synced the
//      finished renders — status stays "generating" with empty resultFileNames).
// Either way we emit a unified ConceptGroup so the views render identically.

import type { EntityStore } from "./entityStore";
import type { CyclePayload, Job } from "./types";

export interface ConceptRender {
  key: string; // stable React key
  /** Content-addressed blob key (synced renders) — resolve via assetResolver. */
  s3Key?: string;
  /** Direct presigned URL (job-fallback renders) — use as <img src>. */
  url?: string;
  /** Local filename of the render (for save-to-ideabook dedupe), when known. */
  fileName?: string;
  /** "Before" source local filename, when known. */
  beforeFileName?: string;
  /** "Before" source image key, when known. */
  beforeS3Key?: string;
  concept: number;
  sourceIndex: number;
}

export interface ConceptGroup {
  conceptIndex: number;
  name: string;
  brief: string;
  renders: ConceptRender[];
}

function parseRenderKey(key: string): { concept: number; sourceIndex: number } {
  let m = key.match(/\/(\d+)\/(\d+)\.png$/);
  if (m) return { concept: Number(m[1]), sourceIndex: Number(m[2]) };
  m = key.match(/\/(\d+)\.png$/);
  if (m) return { concept: 0, sourceIndex: Number(m[1]) };
  return { concept: 0, sourceIndex: 0 };
}

function group(renders: ConceptRender[], names: string[], briefs: string[]): ConceptGroup[] {
  const byConcept = new Map<number, ConceptRender[]>();
  for (const r of renders) {
    const arr = byConcept.get(r.concept) ?? [];
    arr.push(r);
    byConcept.set(r.concept, arr);
  }
  return [...byConcept.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([conceptIndex, rs]) => ({
      conceptIndex,
      name: names[conceptIndex] || `Concept ${conceptIndex + 1}`,
      brief: briefs[conceptIndex] || "",
      renders: rs.sort((a, b) => a.sourceIndex - b.sourceIndex),
    }));
}

/** True when this cycle has no synced renders but points at a recoverable job. */
export function cycleNeedsJobFallback(cycle: CyclePayload): boolean {
  return (cycle.resultFileNames?.length ?? 0) === 0 && !!cycle.jobId;
}

/** Concept groups from the synced cycle (content-addressed blobs). */
export function conceptGroupsFromCycle(cycle: CyclePayload, store: EntityStore): ConceptGroup[] {
  const {
    resultFileNames = [],
    resultConcepts = [],
    resultSourceIndices = [],
    sourceFileNames = [],
    conceptNames = [],
    conceptBriefs = [],
  } = cycle;
  const renders: ConceptRender[] = resultFileNames.map((fileName, p) => {
    const sourceIndex = resultSourceIndices[p] ?? -1;
    const beforeFile = sourceIndex >= 0 ? sourceFileNames[sourceIndex] : undefined;
    return {
      key: `c-${p}`,
      s3Key: store.s3KeyForPhotoFile(fileName),
      fileName,
      beforeFileName: beforeFile,
      beforeS3Key: beforeFile ? store.s3KeyForPhotoFile(beforeFile) : undefined,
      concept: resultConcepts[p] ?? 0,
      sourceIndex,
    };
  });
  return group(renders, conceptNames, conceptBriefs);
}

/** Concept groups from a completed backend job (fallback for un-synced renders). */
export function conceptGroupsFromJob(job: Job): ConceptGroup[] {
  const names = [...job.concepts].sort((a, b) => a.index - b.index).map((c) => c.name ?? "");
  const briefs = [...job.concepts].sort((a, b) => a.index - b.index).map((c) => c.brief ?? "");
  const renders: ConceptRender[] = job.results.map((r) => {
    const meta =
      r.concept != null && r.sourceIndex != null
        ? { concept: r.concept, sourceIndex: r.sourceIndex }
        : parseRenderKey(r.renderKey);
    return {
      key: `j-${r.renderKey}`,
      url: r.url,
      beforeS3Key: r.sourceKey && r.sourceKey.startsWith("users/") ? r.sourceKey : undefined,
      concept: meta.concept,
      sourceIndex: meta.sourceIndex,
    };
  });
  return group(renders, names, briefs);
}

/** Unified resolver: synced renders if present, else the completed job's renders. */
export function resolveConceptGroups(
  cycle: CyclePayload,
  store: EntityStore,
  job: Job | undefined,
): ConceptGroup[] {
  if ((cycle.resultFileNames?.length ?? 0) > 0) return conceptGroupsFromCycle(cycle, store);
  if (job && job.status === "completed") return conceptGroupsFromJob(job);
  return [];
}

/** Effective idea/render count for a cycle (synced or via the job fallback). */
export function cycleRenderCount(cycle: CyclePayload, job: Job | undefined): number {
  const synced = cycle.resultFileNames?.length ?? 0;
  if (synced > 0) return synced;
  if (job && job.status === "completed") return job.results.length;
  return 0;
}
